import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import {
  Row,
  Col,
  Button,
  AutoComplete,
} from 'antd';
import { GoogleApiWrapper } from 'google-maps-react';
import { useSelector } from 'react-redux';
import { ErrorMessage } from '@hookform/error-message';
import { Controller } from 'react-hook-form';
import Select from 'react-select';
import {
  // Country,
  State,
} from 'country-state-city';
import { EditOutlined, SaveFilled } from '@ant-design/icons';
import {
  LabelText,
  Text,
  Input,
} from '../../../design';
import { GOOGLE_APIKEY, PINCODE_PATTERNS, ALPHA2_COUNTRY_MAP } from '../../../appconstants';

type Props = {
  customInputStyle: Object,
  disableStyles: Object,
  customSelectStyle: Object,
  errors: Object,
  control: Object,
  watch: Function,
  trigger: Function,
  register: Function,
  setValue: Function,
  BACKGROUND_COLOR: String,
  google: Object,
  preFillCustomer: Object,
};

const AddressFields = (props: Props) => {
  const {
    customInputStyle,
    customSelectStyle,
    errors,
    control,
    watch,
    trigger,
    register,
    setValue,
    BACKGROUND_COLOR,
    google,
    preFillCustomer,
  } = props;

  useEffect(() => {
    register({ name: 'customerAddressEditState' });
    setValue('customerAddressEditState', false);
  }, [register, setValue]);

  useEffect(() => {
    if (preFillCustomer) {
      const customerAddress = preFillCustomer.address;
      const customerStateName = State?.getStateByCodeAndCountry(customerAddress?.state, customerAddress?.country?.alpha2)
      const customerStateValue = {
        label: customerStateName?.name,
        value: customerAddress?.state,
      };
      const customerCountryValue = {
        label: ALPHA2_COUNTRY_MAP[customerAddress?.country?.alpha2],
        value: customerAddress?.country?.alpha2,
      };
      setValue('customerStreetAddress', customerAddress?.street || '');
      setValue('customerCity', customerAddress?.city || '');
      setValue('customerState', (customerStateValue?.label && customerStateValue) || null);
      setValue('customerPincode', customerAddress?.postalCode || '');
      setValue('customerCountry', (customerCountryValue?.label && customerCountryValue) || null);
      setValue('customerAddressEditState', false);
    }
  }, [preFillCustomer, setValue]);

  const isMobileView = useSelector(state => state.appConfigInfo.isMobileView);
  const [googleMapLocationOptions, setGoogleMapLocationOptions] = useState([]);
  const serviceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  // const allCountries = useMemo(() => Country.getAllCountries().map(country => ({
  //   value: country.isoCode,
  //   label: country.name
  // })), []);
  const allCountries = useMemo(() => ([
    { label: 'USA', value: 'US' },
    { label: 'CANADA', value: 'CA' },
    { label: 'INDIA', value: 'IN' },
  ]), []);
  const getStates = useCallback((countryCode) => {
    return State.getStatesOfCountry(countryCode).map(state => ({
      value: state.isoCode,
      label: state.name
    }));
  }, []);
  const [listOfStates, setListOfStates] = useState([]);
  const selectedCustomerCountry = watch('customerCountry');
  const customerCountryChange = useRef(false);

  useEffect(() => {
    if (customerCountryChange?.current) {
      setValue('customerState', null);
      customerCountryChange.current = false;
    }
    setListOfStates(getStates(selectedCustomerCountry?.value));
  }, [
    selectedCustomerCountry,
    setValue,
    setListOfStates,
    getStates,
  ]);

  const disableStyles = {
    container: base => ({
      ...base,
      width: '100%',
      height: '41px',
      marginTop: '8px',
      marginBottom: '8px',
      borderRadius: '10px',
    }),
    control: base => ({
      ...base,
      height: '41px',
      minHeight: 33,
      border: '1px solid #CCCCCC',
      boxShadow: 'none',
      borderColor: '#CCCCCC !important',
      background: '#f5f5f5',
      color: '#b8b8b8'
    }),
    dropdownIndicator: base => ({
      ...base,
      height: 33,
      minHeight: 33,
    }),
  };

  const editButtonStyle = {
    height: '41px',
    borderColor: '#3E8BFF',
    color: '#3E8BFF',
    marginTop: isMobileView ? '20px' : 0,
    width: isMobileView ? '100%' : 'auto',
  };

  const saveButtonStyle = {
    ...editButtonStyle,
    color: 'white'
  };

  const handleCustomerAddressSaveClick = async () => {
    const fieldsToValidate = [
      'customerStreetAddress',
      'customerCity',
      'customerState',
      'customerPincode',
      'customerCountry',
    ];
    const validityResults = await Promise.all(fieldsToValidate.map(field => trigger(field)));
    const isAllValid = validityResults.every(Boolean);
    if (isAllValid) {
      setIsEditing(false);
      setValue('customerAddressEditState', false);
    }
  };

  const handleCustomerAddressEditClick = async () => {
    setValue('customerAddressEditState', true);
    setIsEditing(true);
  };

  const validatePincode = (value) => {
      const selectedCountryIso = control?.getValues('customerCountry')?.value;
      if (!selectedCountryIso) return 'Invalid pincode format';
      const pattern = PINCODE_PATTERNS[selectedCountryIso.toUpperCase()];
      if (pattern && !pattern.test(value)) {
        return `Invalid pincode format`;
      }
      return true;
    }

  // handle google maps based autocomplete
  useEffect(() => {
    if (google) {
      serviceRef.current = new google.maps.places.AutocompleteService();
      // Dummy div for PlacesService initialization
      placesServiceRef.current = new google.maps.places.PlacesService(document.createElement('div'));
    }
  }, [google]);

  const handleLocationSearch = (value) => {
    if (!value || !serviceRef.current) {
      setGoogleMapLocationOptions([]);
      return;
    }

    serviceRef.current.getPlacePredictions(
      {
        input: value,
        types: ['address'],
        componentRestrictions: {
          country: ['us', 'ca', 'in'], // ISO Alpha-2 codes
        }
      },
      (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          setGoogleMapLocationOptions(predictions.map(p => ({
            value: p.description,
            label: p.description,
            placeId: p.place_id,
          })));
        }
      }
    );
  };

  const onLocationSelect = (value, option) => {
    const request = {
      placeId: option.placeId,
      fields: ['address_components', 'formatted_address'],
    };

    placesServiceRef.current.getDetails(request, (place, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK) {
        const components = place.address_components;
        
        const getComp = (type, member = 'long_name') => {
          const c = components.find(x => x.types.includes(type));
          return c ? c[member] : '';
        };

        const data = {
          full_address: place.formatted_address,
          street: `${getComp('street_number')} ${getComp('route')}`.trim(),
          city: getComp('locality') || getComp('sublocality_level_1'),
          stateName: getComp('administrative_area_level_1', 'long_name'),
          stateIso: getComp('administrative_area_level_1', 'short_name'),
          pincode: getComp('postal_code'),
          countryName: getComp('country', 'long_name'),
          countryAlpha2: getComp('country', 'short_name'),
        };

        // Use RHF's setValue to update the form state
        setValue('customerStreetAddress', value);
        setValue('customerCity', data?.city);
        setValue('customerState', { label: data?.stateName, value: data?.stateIso });
        setValue('customerPincode', data?.pincode);
        setValue('customerCountry', { label: data?.countryName, value: data?.countryAlpha2 });
      }
    });
  };

  return (
    <Row className="mt-1">
      <Col span={24}>
        <Row gutter={24}>
          <Col span={24}>
            <LabelText>Street Address</LabelText>
            <Controller
              defaultValue={""}
              color={errors.customerStreetAddress ? "red" : "inherit"}
              marginBottom="0px"
              control={control}
              name="customerStreetAddress"
              placeholder="Street Address"
              rules={{
                required: 'This field is required'
              }}
              render={({ value, onChange, ref }) => (
                <AutoComplete
                  ref={ref}
                  value={value}
                  onChange={onChange}
                  options={googleMapLocationOptions}
                  onSearch={handleLocationSearch}
                  onSelect={(val, opt) => {
                    onChange(val); // Update RHF value
                    onLocationSelect(val, opt); // Fetch details
                  }}
                  defaultValue=""
                  disabled={!isEditing}
                  allowClear
                  style={{
                    ...customInputStyle,
                    paddingLeft: 0,
                  }}
                  className="mt-2"
                />
              )}
            />
            <ErrorMessage
              errors={errors}
              name="customerStreetAddress"
              render={({ message }) => (
                <Text
                  fontSize="12px"
                  marginTop="4px"
                  marginBottom="0px"
                  color="#ff1e1e"
                >
                  {message}
                </Text>
              )}
            />
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={isMobileView ? 24 : 8}>
            <LabelText>City</LabelText>
            <Controller
              defaultValue={""}
              color={errors.customerCity ? "red" : "inherit"}
              marginBottom="0px"
              control={control}
              name="customerCity"
              placeholder="Street Address"
              rules={{
                required: 'This field is required'
              }}
              as={<Input
                readOnly={!isEditing}
                style={{
                  ...customInputStyle,
                  background: !isEditing ? BACKGROUND_COLOR : '',
                }}
              />}
            />
            <ErrorMessage
              errors={errors}
              name="customerCity"
              render={({ message }) => (
                <Text
                  fontSize="12px"
                  marginTop="4px"
                  marginBottom="0px"
                  color="#ff1e1e"
                >
                  {message}
                </Text>
              )}
            />
          </Col>
          <Col span={isMobileView ? 24 : 8}>
            <LabelText>State</LabelText>
            <Controller
              defaultValue={null}
              color={errors.customerState ? "red" : "inherit"}
              marginBottom="0px"
              control={control}
              name="customerState"
              placeholder="select"
              rules={{
                required: 'State is required'
              }}
              render={({ value, onChange, ref }) => (
                <Select
                  ref={ref}
                  value={value}
                  onChange={(val) => onChange(val)}
                  menuPlacement="auto"
                  isSearchable={isEditing}
                  menuIsOpen={!isEditing ? false : undefined}
                  theme={theme => ({
                    ...theme,
                    borderRadius: "4px 0 0 4px",
                    boxShadow: 0
                  })}
                  getOptionLabel={(option) => option?.label}
                  getOptionValue={(option) => option?.value}
                  styles={!isEditing ? disableStyles : customSelectStyle}
                  options={listOfStates}
                />
              )}
            />
            <ErrorMessage
              errors={errors}
              name="customerState"
              render={({ message }) => (
                <Text
                  fontSize="12px"
                  marginTop="4px"
                  marginBottom="0px"
                  color="#ff1e1e"
                >
                  {message}
                </Text>
              )}
            />
          </Col>
          <Col span={isMobileView ? 24 : 8}>
            <LabelText>Pincode</LabelText>
            <Controller
              defaultValue={""}
              color={errors.customerPincode ? "red" : "inherit"}
              marginBottom="0px"
              control={control}
              name="customerPincode"
              placeholder="Street Address"
              rules={{
                required: 'This field is required',
                validate: validatePincode,
              }}
              as={<Input
                readOnly={!isEditing}
                style={{
                  ...customInputStyle,
                  background: !isEditing ? BACKGROUND_COLOR : '',
                }}
              />}
            />
            <ErrorMessage
              errors={errors}
              name="customerPincode"
              render={({ message }) => (
                <Text
                  fontSize="12px"
                  marginTop="4px"
                  marginBottom="0px"
                  color="#ff1e1e"
                >
                  {message}
                </Text>
              )}
            />
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={isMobileView ? 24 : 8}>
            <LabelText>Country</LabelText>
            <Controller
              defaultValue={""}
              color={errors.customerCountry ? "red" : "inherit"}
              marginBottom="0px"
              control={control}
              name="customerCountry"
              placeholder="select"
              rules={{
                required: 'Country is required'
              }}
              render={({ value, onChange, ref }) => (
                <Select
                  ref={ref}
                  value={value}
                  onChange={(val) => { customerCountryChange.current = true; onChange(val); }}
                  menuPlacement="auto"
                  isSearchable={isEditing}
                  menuIsOpen={!isEditing ? false : undefined}
                  theme={theme => ({
                    ...theme,
                    borderRadius: "4px 0 0 4px",
                    boxShadow: 0
                  })}
                  getOptionLabel={(option) => option.label}
                  styles={!isEditing ? disableStyles : customSelectStyle}
                  options={allCountries}
                />
              )}
            />
            <ErrorMessage
              errors={errors}
              name="customerCountry"
              render={({ message }) => (
                <Text
                  fontSize="12px"
                  marginTop="4px"
                  marginBottom="0px"
                  color="#ff1e1e"
                >
                  {message}
                </Text>
              )}
            />
          </Col>
          <Col span={isMobileView ? 24 : 8}></Col>
          <Col
            span={isMobileView ? 24 : 8}
            style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'flex-end',
              alignItems: isMobileView ? 'flex-start' : 'flex-end',
              paddingBottom: '8px',
            }}
          >
            {!isEditing && (
              <Button
                onClick={handleCustomerAddressEditClick}
                style={editButtonStyle}
                icon={<EditOutlined color="#3E8BFF" />}
              >
                Edit
              </Button>
            )}
            {isEditing && (
              <Button
                onClick={handleCustomerAddressSaveClick}
                icon={<SaveFilled color="white" />}
                style={saveButtonStyle}
                type="primary"
              >
                Save
              </Button>
            )}
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default GoogleApiWrapper({
  apiKey: GOOGLE_APIKEY,
  libraries: ['places'],
})(AddressFields);
